import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { RequesSetupService } from '../../services/request-setup.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { UserBehaviorService } from '../../services/user-events/user-behavior.service';
import { UsersService } from '../../services/users.service';
import { ModalCreateSetupComponent } from '../../modals/modal-create-setup/modal-create-setup.component';
import { BillingInformationComponent } from '../../modals/billing-information/billing-information.component';
import { ModalNewRequestTypeComponent } from '../../modals/modal-new-request-type/modal-new-request-type.component';
import { StatusSetupComponent } from '../../modals/status-setup/status-setup.component';
import { ObservableService } from '../../services/observable.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TableAgGridComponent } from '../../components/table-ag-grid/table-ag-grid.component';
import { TableSkeletonComponent } from '../../components/table-skeleton/table-skeleton.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { TitleBarComponent } from '../../components/title-bar/title-bar.component';
import { BreadcrumbModule } from 'xng-breadcrumb';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { NgIf, NgFor, NgClass, UpperCasePipe, DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-request-setup',
  templateUrl: './request-setup.component.html',
  styleUrls: ['./request-setup.component.css'],
  standalone: true,
  imports: [
    NgIf,
    LoaderOverlayComponent,
    BreadcrumbModule,
    RouterLink,
    TitleBarComponent,
    NgFor,
    NgClass,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    TableSkeletonComponent,
    TableAgGridComponent,
    UpperCasePipe,
    DecimalPipe
  ]
})
export class RequestSetupComponent implements OnInit, OnDestroy {
  setTimeCSV: any;
  showLoaderOverlay: any;

  selectedCard: string = 'created';

  setup: any;
  path: any;
  pageTitle: any;
  noDataFound: any;
  userId: any;
  selectedCardTitle: string | null = null;
  rowDataOriginal: any;
  clickCard: boolean = true;
  orderedCards: any;
  emailAddresses: any;
  shortName: any;

  cardClasses = {
    created: 'bottom-primary',
    activated: 'bottom-active',
    deactivated: 'bottom-forbidden'
  };

  expandedElement: boolean = true;
  transformToggle: boolean = true;

  currentDate: string;

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public dialog: MatDialog,
    private requestService: RequesSetupService,
    private router: Router,
    private userBehavior: UserBehaviorService,
    private userService: UsersService,
    private observableService: ObservableService
  ) {
    this.currentDate = this.getFormattedDate(new Date());

    this.observableService
      .getVariable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        if (value == 'viewDetailsSetup') {
          this.getSetup();
        }
      });
  }

  async ngOnInit(): Promise<void> {
    const currentPath = this.router.url;

    switch (currentPath) {
      case '/teams-setup':
        this.pageTitle = 'Teams Setup';
        this.path = '/api/v1/teams/setups';
        break;
      case '/roles-setup':
        this.pageTitle = 'Roles Setup';
        this.path = '/api/v1/roles/setups';
        break;
      case '/users-setup':
        this.pageTitle = 'Users Setup';
        this.path = '/api/v1/users/setups';
        break;
      case '/task-management/priority-setup':
        this.pageTitle = 'Priority Setup';
        this.path = '/api/v1/priorities/setups';
        break;
      case '/task-management/request-setup':
        this.pageTitle = 'Request Setup';
        this.path = '/api/v1/requests/setups';
        break;
      default:
        this.pageTitle = 'NOT FOUND';
        break;
    }

    this.getSetup();
    const user = await this.userBehavior.fromBehavior();
    this.getUsersByEmail(user?.email);
  }

  getFormattedDate(date: Date): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString();

    return `${month}/${day}/${year}`;
  }

  getSetup() {
    this.clickCard = false;
    this.requestService.getSetup(this.path).subscribe((resp: any) => {
      if (this.router.url == '/users-setup') {
        this.filterUsers(resp);
      } else if (this.router.url == '/task-management/request-setup') {
        this.filterShort(resp);
      }

      const orderedCardKeys = ['created', 'activated', 'inactivated'];

      this.setup = resp;

      this.orderedCards = orderedCardKeys.map((key) => this.setup.cards[key]);
      setTimeout(() => {
        this.clickCard = true;
      }, 10);
      this.rowDataOriginal = this.setup?.table?.data?.rowData;
      if (this.setup === undefined) {
        this.noDataFound = true;
      }
    });
  }

  getUsersByEmail(email: any) {
    this.userService
      .getUsersByEmail(email)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {
        this.userId = resp[0].userId;
      });
  }

  filterUsers(resp: any) {
    this.emailAddresses = resp.table.data.rowData.map((row: any) => row.emailAddress);
  }

  filterShort(resp: any) {
    this.shortName = resp.table.data.rowData.map((row: any) => row.shortName);
  }

  onCardClick(clickedCard: any): void {
    let clickedCardIsUnselected = !clickedCard.selected;
    this.clickCard = false;
    this.setup.table.data.rowData = this.rowDataOriginal;

    this.selectedCardTitle = clickedCard.title;
    let countUnselected = 0;

    for (const cardKey in this.setup.cards) {
      if (this.setup.cards.hasOwnProperty(cardKey)) {
        if (!this.setup.cards[cardKey].selected) {
          countUnselected++;
        }
      }
    }

    if (clickedCardIsUnselected && countUnselected === 1) {
      for (const cardKey in this.setup.cards) {
        if (this.setup.cards.hasOwnProperty(cardKey)) {
          this.selectedCardTitle = 'created';
          this.setup.cards[cardKey].selected = false;
        }
      }
    } else {
      for (const cardKey in this.setup.cards) {
        if (this.setup.cards.hasOwnProperty(cardKey)) {
          this.setup.cards[cardKey].selected =
            this.setup.cards[cardKey].title.toLocaleLowerCase() !== clickedCard.title.toLocaleLowerCase();
        }
      }
    }
    this.applyFilter();
  }

  applyFilter() {
    if (this.selectedCardTitle?.toLowerCase() === 'created') {
      this.setup.table.data.rowData = this.rowDataOriginal;
    } else {
      this.setup.table.data.rowData = this.rowDataOriginal.filter(
        (item: any) =>
          item.statusFlag === this.selectedCardTitle ||
          item.linkedUsers > (this.selectedCardTitle == 'Clients Linked' ? 0 : 2) ||
          item.linkedUsers == (this.selectedCardTitle == 'Clients Not Linked' ? 0 : 1)
      );
    }
    setTimeout(() => {
      this.clickCard = true;
    }, 10);
  }

  onCustomCellClick(event: { actions?: any; data?: any; column?: any; event?: any }) {
    if (event?.column?.toLowerCase() === 'linkedusers') {
      this.openModalComponent(event.event.data.actions.viewDetail);
    } else if (event?.column?.toLowerCase() === 'teams') {
      this.openModalComponent(event.event.data.actionsTeams.viewDetail);
    } else if (event?.column?.toLowerCase() === 'roles') {
      this.openModalComponent(event.event.data.actionsRoles.viewDetail);
    } else if (event?.column?.toLowerCase() === 'requesttypestatus') {
      this.openStatusModal(event);
    }
  }

  openModalComponent(actions: any) {
    const rowData = {
      path: actions.path,
      orderNumber: actions.modalName,
      clientName: actions.clientName,
      modalName: actions.title,
      get: actions.method == 'GET' ? true : false,
      param: 'param',
      env: 'API',
      userId: this.userId,
      changed: false
    };

    const dialogRef = this.dialog.open(BillingInformationComponent, {
      autoFocus: false,
      closeOnNavigation: true,
      maxWidth: '100vw',
      width: '40vw',
      height: '100%',
      panelClass: 'right-modal',
      position: {
        left: '',
        right: '0',
        top: '0'
      },
      data: rowData
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (rowData.changed) {
        this.getSetup();
      }
    });
  }

  createSetup() {
    this.setup.createNewBtn.modal['idUser'] = this.userId;
    if (this.setup.createNewBtn.buttonTitle.toLowerCase() === 'new request') {
      const dialogRef = this.dialog.open(ModalNewRequestTypeComponent, {
        autoFocus: false,
        closeOnNavigation: true,
        width: '60%',
        height: '70%',
        panelClass: '',
        position: {},
        data: {
          modal: this.setup.createNewBtn.modal,
          shortName: this.shortName
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        this.getSetup();
        if (result === 'reabrir') {
          this.createSetup();
        }
      });
    } else {
      const dialogRef = this.dialog.open(ModalCreateSetupComponent, {
        autoFocus: false,
        closeOnNavigation: true,
        width: '60%',
        height: '70%',
        panelClass: '',
        position: {},
        data: {
          modal: this.setup.createNewBtn.modal,
          users: this.emailAddresses
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        this.getSetup();
        if (result === 'reabrir') {
          this.createSetup();
        }
      });
    }
  }

  // status setup
  openStatusModal(event: any) {
    const dialogRef = this.dialog.open(StatusSetupComponent, {
      autoFocus: false,
      closeOnNavigation: true,
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      panelClass: 'fullscreen-modal',
      position: {},
      data: {
        userId: this.userId,
        date: this.currentDate,
        createdBy: event.event.data.createdBy,
        createdDate: event.event.data.creationDate,
        reqId: event.event.data.requestId,
        requestType: event.event.data.requestName
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getSetup();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
